<script setup lang="ts">
import { DateService } from '@/coreDomainsModule/services/dateService';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { i18n } from '@/i18n';
import { useInventoryStore } from '@/InventoryControlModule/pages/useInventoryStore';
import { storeToRefs } from 'pinia';

const t = i18n.t;
const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const { resetCombinedOrderStatus, onConfirmCombinedOrder } = inventoryStore;

const gridTemplateColumns: string[] = ['125px', '125px', '125px', '125px', '125px', '125px', '80px', '125px'];
</script>
<template>
  <InfoModal
    :back-button-label="t('キャンセル')"
    :next-button-label="t('まとめ発注確定')"
    :modal-state="!!state.combinedOrdersState.combinedOrderContents.orderCount"
    :modal-width="1024"
    :is-disabled-next-button="state.combinedOrdersState.combinedOrderContents.groupName === ''"
    no-description
    class="combine-order-comfirm"
    @on-back="resetCombinedOrderStatus"
    @on-next="onConfirmCombinedOrder"
  >
    <template #title> {{ t('まとめ発注内容確認') }} </template>
    <template #default>
      <div class="combine-order-comfirm__section combine-order-comfirm__section--setting">
        <h2 class="section-title">{{ t('まとめ発注グループ設定') }}</h2>
        <div>
          <div class="setting__input__label">
            <label for="combine-order-confirm-input-group" class="setting__input__label-text">{{ t('グループ名') }}*</label>
          </div>
          <input
            id="combine-order-confirm-input-group"
            class="setting__input"
            type="text"
            :value="state.combinedOrdersState.combinedOrderContents.groupName"
            @input="
              (e) => {
                state.combinedOrdersState.combinedOrderContents.groupName = (e.target as HTMLInputElement).value;
              }
            "
          />
        </div>
        <div class="setting__info">
          <div class="setting__info-item">
            <div class="setting__info-text setting__info-text--label">{{ t('発注件数') }}</div>
            <div class="setting__info-text setting__info-text--value">{{ state.combinedOrdersState.combinedOrderContents.orderCount }}</div>
          </div>
          <div class="setting__info-item">
            <div class="setting__info-text setting__info-text--label">{{ t('発注合計数量') }}</div>
            <div class="setting__info-text setting__info-text--value">
              {{ state.combinedOrdersState.combinedOrderContents.totalAmount.addComma() }}
            </div>
          </div>
          <div class="setting__info-item">
            <div class="setting__info-text setting__info-text--label">{{ t('希望納期') }}</div>
            <div class="setting__info-text setting__info-text--value">
              {{
                DateService.convertIYearMonthDayToYYYYMMDDWithSlash(
                  state.combinedOrdersState.combinedOrderContents.desiredDeliveryDate,
                  true
                )
              }}
            </div>
          </div>
          <div class="setting__info-item">
            <div class="setting__info-text setting__info-text--label">{{ t('積載率') }}</div>
            <div class="setting__info-text setting__info-text--value">
              {{ state.combinedOrdersState.combinedOrderContents.loadFactor.roundToDecimal(2).addComma() }}%
            </div>
          </div>
        </div>
      </div>
      <div class="combine-order-comfirm__section combine-order-comfirm__section--detail">
        <h2 class="section-title">{{ t('発注詳細') }}</h2>
        <EzTable :grid-template-columns="gridTemplateColumns" max-height="325px" sticky-header class="detail__table">
          <template #header>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('置場コード') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('置場名') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('仕入先コード') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('仕入先名') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('商品コード') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('商品名') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data detail__table-header-data--unit">{{ t('容量') }}</div>
            </EzHeader>
            <EzHeader class="detail__table-header">
              <div class="detail__table-header-data">{{ t('発注数量') }}</div>
            </EzHeader>
          </template>
          <template #body>
            <EzTableRow
              v-for="(orderWithBusiness, index) in state.combinedOrdersState.combinedOrderContents.confirmedOrdersWithBusinessAndBasis"
              :id="`combine-order-confirm-table-row-${index}`"
              :key="orderWithBusiness.business.businessId"
              :grid-template-columns="gridTemplateColumns"
              class="detail__table-row"
            >
              <EzCell class="detail__table-data">
                {{ orderWithBusiness.business.warehouseNumber }}
              </EzCell>
              <EzCell class="detail__table-data">
                {{ orderWithBusiness.business.warehouse }}
              </EzCell>
              <EzCell class="detail__table-data">
                {{ orderWithBusiness.business.supplierNumber }}
              </EzCell>
              <EzCell class="detail__table-data">
                {{ orderWithBusiness.business.supplierName }}
              </EzCell>
              <EzCell class="detail__table-data">
                {{ orderWithBusiness.business.businessNumber }}
              </EzCell>
              <EzCell class="detail__table-data">
                {{ orderWithBusiness.business.businessName }}
              </EzCell>
              <EzCell force-right class="detail__table-data">
                {{ orderWithBusiness.business.package }}
              </EzCell>
              <EzCell force-right class="detail__table-data">
                {{ orderWithBusiness.order.amount.addComma() }}
              </EzCell>
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.combine-order-comfirm {
  * {
    line-height: 1;
  }
  .section-title {
    font-size: 16px;
    font-weight: 700;
  }
  &__section {
    padding: 16px;
    border-radius: 8px;

    &--setting {
      background-color: $semanticPrimarySubdued;
      display: grid;
      grid-template-rows: auto auto auto;
      gap: 16px;
    }
    &--detail {
      background-color: $backgroundDefault;
      display: grid;
      grid-template-rows: auto auto;
      gap: 16px;
    }
  }
}

.setting {
  &__input {
    padding: 3px 8px;
    background-color: $semanticWhite;
    height: 32px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.27px;
    text-align: left;
    color: $textDefault;
    width: 100%;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    &__label {
      display: grid;
      grid-template-columns: auto auto;
      gap: 3px;
      justify-content: space-between;
      align-items: center;

      &-text {
        padding-bottom: 8px;
        font-family: Noto Sans JP;
        font-size: 14px;
        font-weight: 400;
        line-height: 18.2px;
        text-align: center;

        color: $textDefault;
      }
    }
  }
  &__info {
    display: grid;
    grid-template-columns: repeat(7, auto);
    justify-content: space-between;
    gap: 0;
    &-item {
      // 親Grid内の位置
      @for $i from 1 through 4 {
        &:nth-child(#{$i}) {
          grid-column: #{($i - 1) * 2 + 1};
        }
      }
      width: max-content;
      min-width: 120px;
      display: grid;
      grid-template-rows: auto auto;
      justify-items: center;
      align-items: center;
      gap: 8px;
    }
    &-text {
      width: max-content;
      &--label {
        color: $textSecondary;
      }
      &--value {
        font-size: 18px;
        font-weight: 700;
      }
    }
  }
}

.detail {
  &__table {
    &-header {
      font-size: 14px;
      font-weight: 700;
      line-height: 14px;
      text-align: center;
      color: $textDefault;
      padding: 11px 8px;
      padding: 11px 0;
      white-space: nowrap;
      &-data {
        width: 95px;
        white-space: pre;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: center;
        padding: 2px 0;
        &--unit {
          width: 50px;
        }
      }
    }
    &-data {
      font-size: 14px;
      font-weight: 400;
      line-height: 20.27px;
      text-align: left;
      line-height: 14px;
      color: $textSecondary;
      padding: 11px 8px;
      width: 100%;
      white-space: nowrap;
      padding: 8px 0;
    }
  }
}
</style>
